import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useState } from "react";
import Home from './pages/Home';
import AdminDashboard from './pages/admin/Dashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/user/Dashboard';
import MyJourneyPage from './pages/user/Journey';
import './index.css';
import Contact from './pages/Contact';
import About from './pages/About';
import Benefits from './pages/Benefits';
import Achievements from './pages/Achievements';
import Layout from "./components/Layout";
import Gallery from './pages/Gallery';
import ForgotPassword from "./pages/ForgotPassword"
import ResetPassword from './pages/ResetPassword';
import Courses from './pages/Courses';
import LiveClasses from './pages/LiveClasses';
import Community from './pages/Community';
import Blog from './pages/Blog';
import Trainers from './pages/Trainers';
import PricingPage from './pages/PricingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import RefundPolicy from './pages/RefundPolicy';
import CookiePolicy from './pages/CookiePolicy';
import ProtectedRoute from './routes/ProtectedRoute';
import TrainingLibraryPage from './pages/user/TrainingLibrary';
import LiveSessionsPage from './pages/user/LiveSession';
import DietPlanPage from './pages/user/DietPlan';
import GoalsPage from './pages/user/Goals';
import AchievementsPage from './pages/user/Achievement';
import ProfilePage from './pages/user/Profile';
import NotificationPage from './pages/user/Notification';
import SettingsPage from './pages/user/Setting';
import AttendancePage from './pages/user/Attendance';
import LiveRoom from './pages/user/LiveRoom';
import UserManagementPage from './pages/admin/UserManagement';
import SubscriptionsPage from './pages/admin/Subscriptions';
import ContentManagerPage from './pages/admin/ContentManager';
import AdminLiveSessionsPage from './pages/admin/LiveSessions';
import DietPlanManagerPage from './pages/admin/DietPlanManager';
import HighPriorityUsersPage from './pages/admin/HighPriorityUsers';
import ReportsAnalyticsPage from './pages/admin/ReportsAnalytics';
import AdminNotificationsPage from './pages/admin/Notifications';
import AdminSettingsPage from './pages/admin/Settings';
import UserQueriesPage from './pages/admin/UserQueries';
import TierFeatureGuard from './routes/TierFeatureGuard';
import ScrollToTop from './components/ScrollToTop';
import { AnimatePresence } from "framer-motion";
import PageLoader from "./components/PageLoader";
import AccountDeactivated from "./pages/user/AccountDeactivated";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CheckInProvider } from "./context/CheckInContext";
import { PreferencesProvider } from "./context/PreferencesContext";
import AskDivy from "./components/AskDivy/AskDivy";
import PageInfoGuide from "./components/PageInfoGuide";

function App() {
  const [loading, setLoading] = useState(() => {
    try {
      const cleanPath = (window.location.pathname || "").replace(/\/+$/, "");
      const isRootWebsite = cleanPath === "" || cleanPath === "/";
      const hasVisited = localStorage.getItem("divyyog_website_visited");

      // Strictly show only when user opens the root website for the very first time
      if (isRootWebsite && !hasVisited) {
        localStorage.setItem("divyyog_website_visited", "true");
        return true;
      }
    } catch (e) {
      console.warn("Storage check failed:", e);
    }
    return false;
  });

  return (
    <>
      <AnimatePresence>
        {loading && (
          <PageLoader onComplete={() => {
            setLoading(false);
          }} />
        )}
      </AnimatePresence>
      <Router>
        <PreferencesProvider>
          <CheckInProvider>
            <ScrollToTop />
            <ToastContainer position="top-right" />
            <AskDivy />
            <PageInfoGuide />
            <Routes>
              <Route path="/" element={<Layout> <Home /> </Layout>} />
              <Route path="/about-us" element={<Layout><About /></Layout>} />
              <Route path="/benefits" element={<Layout><Benefits /></Layout>} />
              <Route path="/gallery" element={<Layout><Gallery /></Layout>} />
              <Route path="/milestones" element={<Layout><Achievements /></Layout>} />
              <Route path="/contact" element={<Layout><Contact /></Layout>} />
              <Route path="/courses" element={<Layout><Courses /></Layout>} />
              <Route path="/live-classes" element={<Layout><LiveClasses /></Layout>} />
              <Route path="/community" element={<Layout><Community /></Layout>} />
              <Route path="/blog" element={<Layout><Blog /></Layout>} />
              <Route path="/trainers" element={<Layout><Trainers /></Layout>} />
              <Route path="/instructors" element={<Layout><Trainers /></Layout>} />
              <Route path="/pricing" element={<Layout><PricingPage /></Layout>} />
              <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
              <Route path="/terms-conditions" element={<Layout><TermsConditions /></Layout>} />
              <Route path="/refund-policy" element={<Layout><RefundPolicy /></Layout>} />
              <Route path="/cookie-policy" element={<Layout><CookiePolicy /></Layout>} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />}/>

              {/*User Dashboard Routes */}
              <Route path="/user/dashboard" element={<ProtectedRoute role="user"> <Dashboard /></ProtectedRoute>} />
              <Route path="/user/journey" element={<ProtectedRoute role="user"><TierFeatureGuard featureKey="journey_analytics" featureName="My Journey Analytics"><MyJourneyPage/></TierFeatureGuard></ProtectedRoute>} />
              <Route path="/user/traininglibrary" element={<ProtectedRoute role="user"><TierFeatureGuard featureName="Training Practice Video Library" requireActiveSubOnly={true}><TrainingLibraryPage /></TierFeatureGuard></ProtectedRoute>} />
              <Route path="/user/livesession" element={<ProtectedRoute role="user"><TierFeatureGuard featureName="Live Sessions" requireActiveSubOnly={true}><LiveSessionsPage /></TierFeatureGuard></ProtectedRoute>} />
              <Route path="/live-room/:id" element={<TierFeatureGuard featureName="Live Session Room" requireActiveSubOnly={true}><LiveRoom /></TierFeatureGuard>} />
              <Route path="/user/dietplan" element={<ProtectedRoute role="user"><TierFeatureGuard featureName="Diet & Nutrition Plans" requireActiveSubOnly={true}><DietPlanPage /></TierFeatureGuard></ProtectedRoute>} />
              <Route path="/user/goals" element={<ProtectedRoute role="user"><TierFeatureGuard featureKey="goals_tracker" featureName="Personal Goals"><GoalsPage /></TierFeatureGuard></ProtectedRoute>} />
              <Route path="/user/achievements" element={<ProtectedRoute role="user"><TierFeatureGuard featureKey="achievements" featureName="Achievements & Badges"><AchievementsPage /></TierFeatureGuard></ProtectedRoute>} />
              <Route path="/user/profile" element={<ProtectedRoute role="user"><ProfilePage /></ProtectedRoute>} />
              <Route path="/account-deactivated" element={<ProtectedRoute role="user"><AccountDeactivated /></ProtectedRoute>}/>
              <Route path="/user/notification" element={<ProtectedRoute role="user"><NotificationPage /></ProtectedRoute>} />
              <Route path="/user/settings" element={<ProtectedRoute role="user"><SettingsPage /></ProtectedRoute>} />
              <Route path="/user/setting" element={<ProtectedRoute role="user"><SettingsPage /></ProtectedRoute>} />
              <Route path="/user/attendance" element={<ProtectedRoute role="user"><TierFeatureGuard featureKey="attendance_logs" featureName="Attendance Logs"><AttendancePage /></TierFeatureGuard></ProtectedRoute>} />

              {/*Admin Dashboard Routes */}
              <Route path="/admin/dashboard" element={<ProtectedRoute role="admin"> <AdminDashboard /> </ProtectedRoute>} />
              <Route path="/admin/usermanagement" element={<ProtectedRoute role="admin"> <UserManagementPage /> </ProtectedRoute>} />  
              <Route path="/admin/users" element={<ProtectedRoute role="admin"> <UserManagementPage /> </ProtectedRoute>} />  
              <Route path="/admin/subscription" element={<ProtectedRoute role="admin"> <SubscriptionsPage /> </ProtectedRoute>} />  
              <Route path="/admin/subscriptions" element={<ProtectedRoute role="admin"> <SubscriptionsPage /> </ProtectedRoute>} />  
              <Route path="/admin/contentmanager" element={<ProtectedRoute role="admin"> <ContentManagerPage /> </ProtectedRoute>} />  
              <Route path="/admin/livesession" element={<ProtectedRoute role="admin"> <AdminLiveSessionsPage /> </ProtectedRoute>} />  
              <Route path="/admin/livesessions" element={<ProtectedRoute role="admin"> <AdminLiveSessionsPage /> </ProtectedRoute>} />  
              <Route path="/admin/dietplans" element={<ProtectedRoute role="admin"> <DietPlanManagerPage /> </ProtectedRoute>} />  
              <Route path="/admin/highpriorityuser" element={<ProtectedRoute role="admin"> <HighPriorityUsersPage /> </ProtectedRoute>} />
              <Route path="/admin/highpriorityusers" element={<ProtectedRoute role="admin"> <HighPriorityUsersPage /> </ProtectedRoute>} />
              <Route path="/admin/high-priority-users" element={<ProtectedRoute role="admin"> <HighPriorityUsersPage /> </ProtectedRoute>} />
              <Route path="/admin/priority" element={<ProtectedRoute role="admin"> <HighPriorityUsersPage /> </ProtectedRoute>} />
              <Route path="/admin/reports&analytics" element={<ProtectedRoute role="admin"> <ReportsAnalyticsPage /> </ProtectedRoute>} />  
              <Route path="/admin/notifications" element={<ProtectedRoute role="admin"> <AdminNotificationsPage /> </ProtectedRoute>} />  
              <Route path="/admin/queries" element={<ProtectedRoute role="admin"> <UserQueriesPage /> </ProtectedRoute>} />
              <Route path="/admin/user-queries" element={<ProtectedRoute role="admin"> <UserQueriesPage /> </ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute role="admin"> <AdminSettingsPage /> </ProtectedRoute>} />    
            </Routes>
          </CheckInProvider>
        </PreferencesProvider>
      </Router>
    </>
  );
}

export default App;
